import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubIcon from '@mui/icons-material/Hub';
import MemoryIcon from '@mui/icons-material/Memory';
import PeopleIcon from '@mui/icons-material/People';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';

import { getAdminSummary } from '../../services/adminService';

type Severity = 'success' | 'info' | 'warning' | 'error';

interface ActionItem {
  id: string;
  title: string;
  detail: string;
  count: number;
  severity: Severity;
  actionLabel: string;
  route: string;
  icon: React.ReactNode;
}

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 800,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.78rem',
  fontWeight: 800,
};

const healthColor = (severity: Severity, theme: any) => {
  if (severity === 'success') return theme.palette.success.main;
  if (severity === 'warning') return theme.palette.warning.main;
  if (severity === 'error') return theme.palette.error.main;
  return theme.palette.info.main;
};

const pct = (value?: number) => Math.round(Number(value || 0));

const SummaryMetric = ({ label, value, helper, color, onClick }: {
  label: string;
  value: React.ReactNode;
  helper?: string;
  color: string;
  onClick?: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      minWidth: 126,
      borderLeft: `3px solid ${color}`,
      pl: 1.25,
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.2rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{helper}</Typography>}
  </Box>
);

const ActionQueueRow = ({ item, onOpen }: { item: ActionItem; onOpen: (route: string) => void }) => {
  const theme = useTheme();
  const color = healthColor(item.severity, theme);

  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 1.25, borderLeft: `3px solid ${color}` }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', bgcolor: alpha(color, 0.1), color }}>
            {item.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 900 }}>{item.title}</Typography>
              <Chip label={item.count} size="small" color={item.severity === 'info' ? 'default' : item.severity} sx={{ borderRadius: 0, height: 20, fontSize: '0.64rem', fontWeight: 900 }} />
            </Stack>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{item.detail}</Typography>
          </Box>
        </Stack>
        <Button size="small" variant="outlined" onClick={() => onOpen(item.route)} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          {item.actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
};

export const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await getAdminSummary();
      setStats(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, []);

  const cpu = pct(stats?.server?.cpuUsage);
  const ram = pct(stats?.server?.ramUsage);
  const disk = pct(stats?.server?.diskUsage || 0);
  const pendingApprovals = Number(stats?.accountCreation?.awaiting || 0);
  const liveInputs = Number(stats?.streams?.live || 0);
  const totalOutputs = Number(stats?.streams?.outputs?.total || 0);
  const totalUsers = Number(stats?.users?.total || 0);
  const activeUsers = Number(stats?.users?.active || 0);

  const systemSeverity: Severity = cpu >= 90 || ram >= 90 || disk >= 90 ? 'error' : cpu >= 80 || ram >= 80 || disk >= 80 ? 'warning' : 'success';

  const actionItems = useMemo<ActionItem[]>(() => {
    const items: ActionItem[] = [
      {
        id: 'approvals',
        title: 'Pending account approvals',
        detail: 'Users waiting for admin review before they can operate channels.',
        count: pendingApprovals,
        severity: pendingApprovals > 0 ? 'warning' : 'success',
        actionLabel: 'Review users',
        route: '/admin/users',
        icon: <AssignmentTurnedInIcon fontSize="small" />,
      },
      {
        id: 'billing',
        title: 'Finance review queue',
        detail: 'Open payment, invoice, refund, and manual billing workflows.',
        count: Number(stats?.billing?.pending || stats?.payments?.pending || 0),
        severity: Number(stats?.billing?.failed || stats?.payments?.failed || 0) > 0 ? 'error' : 'info',
        actionLabel: 'Open billing',
        route: '/admin/billing',
        icon: <ReceiptLongIcon fontSize="small" />,
      },
      {
        id: 'noc',
        title: 'Live stream operations',
        detail: 'Inspect inputs, protocols, bandwidth, and emergency stream controls.',
        count: liveInputs,
        severity: liveInputs > 0 ? 'info' : 'success',
        actionLabel: 'Open NOC',
        route: '/admin/streams',
        icon: <PlayCircleOutlineIcon fontSize="small" />,
      },
      {
        id: 'system',
        title: 'Runtime health controls',
        detail: `CPU ${cpu}% · RAM ${ram}% · Disk ${disk}%`,
        count: systemSeverity === 'success' ? 0 : 1,
        severity: systemSeverity,
        actionLabel: 'Open system',
        route: '/admin/system',
        icon: systemSeverity === 'success' ? <SecurityIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />,
      },
    ];

    return items.sort((a, b) => {
      const weight = { error: 4, warning: 3, info: 2, success: 1 } as Record<Severity, number>;
      return weight[b.severity] - weight[a.severity] || b.count - a.count;
    });
  }, [cpu, disk, liveInputs, pendingApprovals, ram, stats, systemSeverity]);

  const regData = stats ? [
    { name: 'Today', count: stats.accountCreation?.today || 0 },
    { name: '7 Days', count: stats.accountCreation?.thisWeek || 0 },
    { name: 'Month', count: stats.accountCreation?.thisMonth || 0 },
    { name: 'Pending', count: pendingApprovals },
  ] : [];

  if (loading && !stats) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={34} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">Loading admin operations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1800, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <DashboardIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>Admin Operations</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Action-first platform control</Typography>
              </Box>
            </Stack>
            <SummaryMetric label="Health" value={systemSeverity.toUpperCase()} helper={`CPU ${cpu}% · RAM ${ram}%`} color={healthColor(systemSeverity, theme)} onClick={() => navigate('/admin/system')} />
            <SummaryMetric label="Live Inputs" value={liveInputs} helper={`${stats?.streams?.protocols?.srt || 0} SRT · ${stats?.streams?.protocols?.rtmp || 0} RTMP`} color={theme.palette.success.main} onClick={() => navigate('/admin/streams')} />
            <SummaryMetric label="Outputs" value={totalOutputs} helper={`${stats?.streams?.outputs?.managed || 0} managed · ${stats?.streams?.outputs?.direct || 0} direct`} color={theme.palette.secondary.main} onClick={() => navigate('/admin/streams')} />
            <SummaryMetric label="Approvals" value={pendingApprovals} helper="awaiting review" color={pendingApprovals ? theme.palette.warning.main : theme.palette.success.main} onClick={() => navigate('/admin/users')} />
            <SummaryMetric label="Users" value={totalUsers} helper={`${activeUsers} active paid`} color={theme.palette.info.main} onClick={() => navigate('/admin/users')} />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={labelSx}>Last updated</Typography>
              <Typography sx={monoSx}>{lastUpdated.toLocaleTimeString([], { hour12: false })}</Typography>
            </Box>
            <Tooltip title="Refresh admin dashboard">
              <span>
                <IconButton size="small" onClick={fetchStats} disabled={loading} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {systemSeverity !== 'success' && (
        <Alert severity={systemSeverity === 'warning' ? 'warning' : 'error'} sx={{ mb: 1.5, borderRadius: 0 }}>
          System resources require attention. CPU {cpu}%, RAM {ram}%, Disk {disk}%.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.15fr 0.85fr' }, gap: 1.5 }}>
        <Stack spacing={1.25}>
          <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <AssignmentTurnedInIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 900 }}>Action Queue</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Highest priority admin work first.</Typography>
              </Box>
            </Stack>
            <Stack spacing={1}>
              {actionItems.map((item) => <ActionQueueRow key={item.id} item={item} onOpen={navigate} />)}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <HubIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 900 }}>Runtime Breakdown</Typography>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
              {[
                ['RTMP', stats?.streams?.protocols?.rtmp || 0, theme.palette.warning.main],
                ['SRT', stats?.streams?.protocols?.srt || 0, theme.palette.secondary.main],
                ['Remapped', stats?.streams?.outputs?.managed || 0, theme.palette.success.main],
                ['HLS', stats?.streams?.outputs?.hls || 0, theme.palette.primary.main],
                ['HLS+', stats?.streams?.outputs?.hlsScte || 0, theme.palette.info.main],
              ].map(([label, value, color]) => (
                <Box key={String(label)} sx={{ border: '0.5px solid', borderColor: 'divider', p: 1, borderLeft: `3px solid ${color}` }}>
                  <Typography sx={labelSx}>{label}</Typography>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 900 }}>{value as number}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Stack>

        <Stack spacing={1.5}>
          <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <MemoryIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 900 }}>System Load</Typography>
            </Stack>
            <Stack spacing={1.35}>
              {[
                ['CPU', cpu, cpu >= 90 ? 'error.main' : cpu >= 80 ? 'warning.main' : 'success.main'],
                ['RAM', ram, ram >= 90 ? 'error.main' : ram >= 80 ? 'warning.main' : 'success.main'],
                ['Disk', disk, disk >= 90 ? 'error.main' : disk >= 80 ? 'warning.main' : 'info.main'],
              ].map(([label, value, color]) => (
                <Box key={String(label)}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={labelSx}>{label}</Typography>
                    <Typography sx={monoSx}>{value}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={Number(value)} sx={{ height: 5, borderRadius: 0, bgcolor: 'action.disabledBackground', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx, p: 1.5, height: 300, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <PeopleIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 900 }}>Growth Queue</Typography>
            </Stack>
            <Box sx={{ width: '100%', minWidth: 1, height: 220, minHeight: 220 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <BarChart data={regData} margin={{ top: 10, right: 14, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.35)} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <ChartTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 0 }} />
                  <Bar dataKey="count" fill={theme.palette.primary.main} radius={[0, 0, 0, 0]} barSize={46} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Stack>
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mt: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>Admin dashboard refreshes every 15 seconds. Use action queue for triage; charts are secondary.</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => navigate('/admin/billing')} sx={{ borderRadius: 0, textTransform: 'none' }}>Finance</Button>
            <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate('/admin/system')} sx={{ borderRadius: 0, textTransform: 'none' }}>System</Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};
